import type { GameState } from '@/features/game/types';

// Old app localStorage structure
interface OldAppState {
  board: string[][];
  boardValidationGrid: string[][];
  currAttempt: { attempt: number; letterPos: number };
  wordSet: string[];
  correctWord: string;
  alertText: string;
  question: string;
  disabledLetters: string[];
  shakeRow: boolean;
  gameOver: { gameOver: boolean; guessedWord: boolean };
  mode: 'light' | 'dark';
  constrast: string; // Note: typo in old app
  winAttempt: {
    first: number;
    second: number;
    third: number;
    fourth: number;
    winAlert: string;
  };
  attempt: {
    try1: string;
    try2: string;
    try3: string;
    try4: string;
  };
  wins: string[];
  played: string[];
  activeAlert: { alert: boolean };
}

// Migration version tracking
const MIGRATION_VERSION_KEY = 'riddle-v2:migration-version';
const CURRENT_MIGRATION_VERSION = 1;

// Old app keys
const OLD_STATE_KEY = 'state';

// New app keys
const NEW_GAME_STATE_KEY = 'riddle-v2:game-state';
const NEW_WINS_KEY = 'riddle-v2:wins';
const NEW_PLAYED_KEY = 'riddle-v2:played';
const NEW_WIN_ATTEMPTS_KEY = 'riddle-v2:win-attempts';
const NEW_SETTINGS_KEY = 'riddle-v2:settings';

/**
 * Check if migration has been completed
 */
export const hasMigrationBeenCompleted = (): boolean => {
  try {
    const version = localStorage.getItem(MIGRATION_VERSION_KEY);
    return version !== null && parseInt(version) >= CURRENT_MIGRATION_VERSION;
  } catch {
    return false;
  }
};

/**
 * Mark migration as completed
 */
const markMigrationCompleted = (): void => {
  try {
    localStorage.setItem(MIGRATION_VERSION_KEY, CURRENT_MIGRATION_VERSION.toString());
  } catch (error) {
    console.warn('Failed to mark migration as completed:', error);
  }
};

/**
 * Convert old validation grid format to new format
 * Old: ["🟩", "🟧", "⬜"] (emojis)
 * New: ["correct", "present", "absent"] (strings)
 */
const convertValidationGrid = (oldGrid: string[][]): string[][] => {
  return oldGrid.map(row =>
    row.map(cell => {
      switch (cell) {
        case '🟩':
          return 'correct';
        case '🟧':
          return 'present';
        case '⬜':
        default:
          return 'absent';
      }
    })
  );
};

/**
 * Convert old attempt format to new format
 * Old: { attempt: 0, letterPos: 0 }
 * New: { attempt: 0, letterPos: 0 } (same structure)
 */
const convertAttempt = (oldAttempt: { attempt: number; letterPos: number }) => {
  return {
    attempt: oldAttempt.attempt,
    letterPos: oldAttempt.letterPos,
  };
};

/**
 * Convert old game over format to new format
 * Old: { gameOver: boolean, guessedWord: boolean }
 * New: { gameOver: boolean, guessedWord: boolean } (same structure)
 */
const convertGameOver = (oldGameOver: { gameOver: boolean; guessedWord: boolean }) => {
  return {
    gameOver: oldGameOver.gameOver,
    guessedWord: oldGameOver.guessedWord,
  };
};

/**
 * Convert old win attempts format to new format
 * Old: { first: number, second: number, third: number, fourth: number, winAlert: string }
 * New: { first: number, second: number, third: number, fourth: number, winAlert: string } (same structure)
 */
const convertWinAttempts = (oldWinAttempts: {
  first: number;
  second: number;
  third: number;
  fourth: number;
  winAlert: string;
}) => {
  return {
    first: oldWinAttempts.first,
    second: oldWinAttempts.second,
    third: oldWinAttempts.third,
    fourth: oldWinAttempts.fourth,
    winAlert: oldWinAttempts.winAlert,
  };
};

/**
 * Convert old settings to new format
 */
const convertSettings = (oldState: OldAppState) => {
  return {
    theme: (oldState.mode === 'dark' ? 'dark' : 'light') as 'light' | 'dark' | 'system',
    contrast: (oldState.constrast === 'highcontrast' ? 'highcontrast' : 'nocontrast') as 'nocontrast' | 'highcontrast',
  };
};

/**
 * Migrate old app data to new format
 */
export const migrateOldAppData = (): {
  gameState: Partial<GameState>;
  wins: string[];
  played: string[];
  winAttempts: GameState['winAttempt'];
  settings: { theme: 'light' | 'dark' | 'system'; contrast: 'nocontrast' | 'highcontrast' };
} => {
  try {
    // Check if old data exists
    const oldStateData = localStorage.getItem(OLD_STATE_KEY);
    if (!oldStateData) {
      console.log('No old app data found to migrate');
      return {
        gameState: {},
        wins: [],
        played: [],
        winAttempts: { first: 0, second: 0, third: 0, fourth: 0, winAlert: '' },
        settings: { theme: 'system', contrast: 'nocontrast' },
      };
    }

    // Parse old state
    const oldState: OldAppState = JSON.parse(oldStateData);
    console.log('Found old app data, migrating...', oldState);

    // Convert data to new format
    const migratedGameState: Partial<GameState> = {
      board: oldState.board || Array(4).fill(null).map(() => Array(6).fill('')),
      boardValidationGrid: oldState.boardValidationGrid 
        ? convertValidationGrid(oldState.boardValidationGrid)
        : Array(4).fill(null).map(() => Array(6).fill('')),
      currentAttempt: oldState.currAttempt 
        ? convertAttempt(oldState.currAttempt)
        : { attempt: 0, letterPos: 0 },
      wordSet: oldState.wordSet || [],
      correctWord: oldState.correctWord || '',
      question: oldState.question || '',
      disabledLetters: oldState.disabledLetters || [],
      shakeRow: oldState.shakeRow || false,
      gameOver: oldState.gameOver 
        ? convertGameOver(oldState.gameOver)
        : { gameOver: false, guessedWord: false },
      attempt: oldState.attempt || { try1: '', try2: '', try3: '', try4: '' },
      winAttempt: oldState.winAttempt 
        ? convertWinAttempts(oldState.winAttempt)
        : { first: 0, second: 0, third: 0, fourth: 0, winAlert: '' },
      wins: oldState.wins || [],
      played: oldState.played || [],
      alertText: oldState.alertText || '',
      activeAlert: oldState.activeAlert || { alert: false },
      mode: oldState.mode === 'dark' ? 'dark' : 'light',
      contrast: oldState.constrast === 'highcontrast' ? 'highcontrast' : 'nocontrast',
    };

    const migratedData = {
      gameState: migratedGameState,
      wins: oldState.wins || [],
      played: oldState.played || [],
      winAttempts: oldState.winAttempt 
        ? convertWinAttempts(oldState.winAttempt)
        : { first: 0, second: 0, third: 0, fourth: 0, winAlert: '' },
      settings: convertSettings(oldState),
    };

    // Save migrated data to new keys
    try {
      localStorage.setItem(NEW_GAME_STATE_KEY, JSON.stringify(migratedGameState));
      localStorage.setItem(NEW_WINS_KEY, JSON.stringify(migratedData.wins));
      localStorage.setItem(NEW_PLAYED_KEY, JSON.stringify(migratedData.played));
      localStorage.setItem(NEW_WIN_ATTEMPTS_KEY, JSON.stringify(migratedData.winAttempts));
      localStorage.setItem(NEW_SETTINGS_KEY, JSON.stringify(migratedData.settings));

      // Mark migration as completed
      markMigrationCompleted();

      console.log('Migration completed successfully');
    } catch (error) {
      console.error('Failed to save migrated data:', error);
    }

    return migratedData;
  } catch (error) {
    console.error('Migration failed:', error);
    return {
      gameState: {},
      wins: [],
      played: [],
      winAttempts: { first: 0, second: 0, third: 0, fourth: 0, winAlert: '' },
      settings: { theme: 'system', contrast: 'nocontrast' },
    };
  }
};

/**
 * Clean up old data after successful migration
 */
export const cleanupOldData = (): void => {
  try {
    localStorage.removeItem(OLD_STATE_KEY);
    console.log('Old app data cleaned up');
  } catch (error) {
    console.warn('Failed to cleanup old data:', error);
  }
};

/**
 * Run migration if needed
 */
export const runMigrationIfNeeded = (): {
  gameState: Partial<GameState>;
  wins: string[];
  played: string[];
  winAttempts: GameState['winAttempt'];
  settings: { theme: 'light' | 'dark' | 'system'; contrast: 'nocontrast' | 'highcontrast' };
} => {
  if (hasMigrationBeenCompleted()) {
    console.log('Migration already completed, skipping...');
    return {
      gameState: {},
      wins: [],
      played: [],
      winAttempts: { first: 0, second: 0, third: 0, fourth: 0, winAlert: '' },
      settings: { theme: 'system', contrast: 'nocontrast' },
    };
  }

  console.log('Running migration from old app...');
  const migratedData = migrateOldAppData();
  
  // Clean up old data after successful migration
  setTimeout(() => {
    cleanupOldData();
  }, 1000); // Small delay to ensure migration is complete

  return migratedData;
};
